import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ColumnsType } from "antd/lib/table";
import { Button, Col, Select, Tooltip } from "antd";
import { EditOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import Table from "../../components/Table";
import ContractorModal from "./ContractorModal";
import api from "../../services/api";
import { ICustomer } from "../Customers";
import { Filters, FiltersCard } from "../../components/Filters";
import { Label } from "../../components/ui";
import { useToast } from "../../hooks/toast";
import filterOptions from "../../utils/filterOptions";

export interface IContractor {
  name: string;
  email: string;
  phone_number?: string;
  customer_id: string;
  id: string;
  customer: ICustomer;
}

const Contractor = () => {
  const [contractors, setContractors] = useState<IContractor[]>([]);
  const [modalContractorVisible, setModalContractorVisible] = useState(false);
  const [selectConstractor, setSelectContractor] = useState<
    IContractor | undefined
  >(undefined);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [customers, setCustomers] = useState<ICustomer[]>([]);

  // filters
  const [customersFilters, setCustomersFilters] = useState<string | undefined>(
    undefined,
  );

  const { t } = useTranslation(["customers"]);
  const { addToast } = useToast();

  const paramsContractor = useMemo(() => {
    return {
      customer_id: customersFilters,
    };
  }, [customersFilters]);

  useEffect(() => {
    setLoading(true);
    api
      .get("/contractors", {
        params: paramsContractor,
      })
      .then(response => {
        setContractors(response.data);
      })
      .finally(() => setLoading(false));
    /* setLoading(old => !old); */
  }, [paramsContractor]);

  useEffect(() => {
    api
      .get<ICustomer[]>("/customers")
      .then(response => {
        setCustomers(response.data);
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro na busca",
          description:
            "Occoreu um erro ao buscar os clientes, tente novamente.",
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCloseModal = useCallback(
    (contractor?: IContractor) => {
      if (contractor) {
        const index = contractors.findIndex(
          contractorFind => contractorFind.id === contractor.id,
        );
        if (index >= 0) {
          const newContractors = [...contractors];
          newContractors[index] = contractor;
          setContractors(newContractors);
        } else {
          setContractors(old => [...old, contractor]);
        }
      }
      setModalContractorVisible(old => !old);
      setSelectContractor(undefined);
    },
    [contractors],
  );

  const handleEditModal = useCallback((contractor: IContractor) => {
    setModalContractorVisible(old => !old);
    setSelectContractor(contractor);
  }, []);

  const columnsContractor: ColumnsType<IContractor> = [
    {
      title: `${t("contractors.name")}`,
      dataIndex: "name",
    },
    {
      title: `${t("contractors.email")}`,
      dataIndex: "email",
    },
    {
      title: `${t("contractors.phone_number")}`,
      dataIndex: "phone_number",
    },
    {
      title: `${t("contractors.customer")}`,
      dataIndex: "customer",
      render: (customer: ICustomer) => {
        return customer?.name;
      },
    },
    {
      title: t("contractors.actions.label"),
      dataIndex: "actions",
      fixed: "right",
      render: (_, record) => {
        return (
          <>
            <Tooltip title={t("contractors.actions.edit")}>
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => handleEditModal(record)}
              />
            </Tooltip>
          </>
        );
      },
    },
  ];

  const contractorExtraAction = useMemo(() => {
    return (
      <Tooltip title={t("contractors.actions.create")}>
        <Button
          icon={<PlusCircleOutlined />}
          type="link"
          size="large"
          onClick={() => setModalContractorVisible(true)}
        />
      </Tooltip>
    );
  }, [t]);

  const handleShowFilters = useCallback(
    value => {
      setShowFilters(value);
      // As the 'showFilters' has not changed yet, within this function,
      // its logic is the reverse to clear all filters (false = true)
      if (showFilters) {
        setCustomersFilters(undefined);
      }
    },
    [showFilters],
  );

  const customersOptions = useMemo(() => {
    if (!customers) {
      return undefined;
    }

    return customers
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(customer => ({
        label: customer.name,
        value: customer.id,
      }));
  }, [customers]);

  const extraFilters = useMemo(
    () =>
      showFilters ? (
        <FiltersCard>
          <Col lg={12} md={12} sm={24} xl={9} xs={24}>
            <div style={{ marginBottom: 5 }}>
              <Label color="#575962">{t("contractors.customer")}</Label>
            </div>
            <Select
              placeholder={t("contractors.customer")}
              style={{ width: "100%" }}
              allowClear
              showSearch
              onChange={value => setCustomersFilters(value)}
              options={customersOptions}
              filterOption={filterOptions}
            />
          </Col>
        </FiltersCard>
      ) : (
        <></>
      ),
    [showFilters],
  );

  return (
    <>
      <ContractorModal
        visible={modalContractorVisible}
        handleCloseModal={handleCloseModal}
        selectedContractor={selectConstractor}
      />
      <Table
        data={contractors}
        columns={columnsContractor}
        loading={loading}
        title={
          <>
            {t("contractors.title")}
            <Filters
              showFilters={showFilters}
              setShowFilters={handleShowFilters}
            />
          </>
        }
        extraActions={contractorExtraAction}
        extraFilters={extraFilters}
      />
    </>
  );
};
export default Contractor;
